/**
 * SA-459 (workflow UX sweep spec §3.3, D9): the record lane's one section
 * card — the wireframe's `details.sec`. A header row with a chevron, an
 * uppercase title and a one-line closed summary that turns `invisible`
 * (never `hidden`) when open, so the header never changes height; the open
 * state remembered per section per viewer in localStorage (a per-device
 * convenience, `CollapsibleSection`'s `storageKey` precedent); a `focused`
 * ring for a `?panel=` deep link, which also opens the section without
 * writing the memory. `collapsible={false}` is the process band: no
 * chevron, always open. The closed line is the section's own sentence,
 * written by the card that owns the data (spec §7.4).
 *
 * SA-1059 (UX pass spec move 1): two kinds of block. `chrome="section"`
 * (the default) is the ONE section recipe — a white surface on the page
 * ground, `rounded-lg shadow-xs`, a border on the `--section-border` token,
 * a tinted header band with a hairline toward the body when open, the title
 * 12.5px semibold. `chrome="quiet"` is a block with no box: an uppercase
 * group label (the treatment a section title had before this pass), a
 * hairline, the content — Activity, Notes and Record. A quiet block still
 * collapses, remembers its state and answers `?panel=`; only the box is
 * gone. The root says which it is (`data-block`).
 *
 * A default that arrives after mount applies until the person toggles, a
 * stored state exists or a deep link opens the section (SA-1077).
 *
 * SA-1418 (R1): `activeTaskFor` names the record whose active task this
 * section holds; the section opens over a stored closed state at mount and
 * once each time the token changes to a new non-null value, never writing the
 * memory and never closing a section. A second `?panel=` press lands the
 * section again through `panelLanding.useOpenPanel` (A2): one landing body
 * serves a `focused` change and a request, never the router's location key.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode, type Ref } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils'
import { LANDING_RING } from './landingRing'
import { onLandingRequest } from './panelLanding'
import { tourAnchor, type TourAnchorId } from '@/components/tour/tourAnchor'

export const RECORD_SECTION_STORAGE_PREFIX = 'record-section:'

/** How long a focused section follows the page's layout after a deep link. */
export const FOCUS_FOLLOW_MS = 3000
/** Clearance below the sticky strip when landing a focused section under it. */
const STICKY_GAP_PX = 12
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const

export interface RecordSectionProps {
  /** The storage key's suffix and the `data-panel` a `?panel=` names. */
  id: string
  title: string
  /** One line, the section's own sentence; a node so a card can put a chip in it. */
  summary?: ReactNode
  defaultOpen?: boolean
  /** Deep-linked (`?panel=`): opened first, landed under the strip, and ringed for FOCUS_FOLLOW_MS — a landing cue, never a state (SA-1034 PR 5, spec §3.8). */
  focused?: boolean
  /** Rendered at the right of the header, outside the toggle button. */
  actions?: ReactNode
  /** Render an `<aside>` (default `<section>`). */
  aside?: boolean
  /** `false` = the process band: no chevron, always open. */
  collapsible?: boolean
  /** Ignored with `collapsible={false}` (the band): a non-collapsible section is always open.
   *  Closed for good — the toggle is disabled and the summary says why (a restricted card, spec §7.4). */
  locked?: boolean
  /** `section` (default): the boxed recipe. `quiet`: no box — a group label, a hairline, the content. */
  chrome?: 'section' | 'quiet'
  className?: string
  sectionRef?: Ref<HTMLElement>
  /** SA-1314: the tour anchor this section carries, if a tour points at it. */
  tour?: TourAnchorId
  /** SA-1418 (R1): the record whose active task this section holds, else null. A new non-null
   *  value opens the section once, over a stored closed state, without writing the memory. */
  activeTaskFor?: string | null
  /** SA-1418 (A1): the header's controls drop under the title when they do not fit beside it. */
  wrapHeader?: boolean
  children: ReactNode
}

function readStored(key: string, fallback: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(key)
    if (stored === 'open') return true
    if (stored === 'closed') return false
  } catch {
    // A private window or blocked site data: the default wins.
  }
  return fallback
}

function hasStored(key: string): boolean {
  try {
    const stored = window.localStorage.getItem(key)
    return stored === 'open' || stored === 'closed'
  } catch {
    return false
  }
}

function writeStored(key: string, open: boolean): void {
  try {
    window.localStorage.setItem(key, open ? 'open' : 'closed')
  } catch {
    // Not remembered; the section still toggles.
  }
}

export function RecordSection({
  id,
  title,
  summary,
  defaultOpen = true,
  focused = false,
  actions,
  aside = false,
  collapsible = true,
  locked = false,
  chrome = 'section',
  className,
  sectionRef,
  tour,
  activeTaskFor = null,
  wrapHeader = false,
  children,
}: RecordSectionProps) {
  const bodyId = useId()
  const storageKey = `${RECORD_SECTION_STORAGE_PREFIX}${id}`
  // A section mounted under a deep link starts open (SA-1191), as `landing` below starts
  // ringing (SA-1190): the follow effect's setStored(true) renders a Scheduler task after the
  // mounting commit, so a section stored closed, or closed by default, painted one frame as a
  // closed header with the ring on it. Starting open writes nothing; only the person's toggle
  // writes the memory.
  const [stored, setStored] = useState(() => focused || Boolean(activeTaskFor) || readStored(storageKey, defaultOpen))
  // SA-1077 (spec D4): the record lane's defaults come from the process read,
  // which lands AFTER the sections mount — so a changed `defaultOpen` still
  // opens an unsettled section (no stored state at mount, no toggle by the
  // person, no deep link) and settles it; a late default never closes one,
  // so a section the lane opened stays open when the process moves on.
  const settled = useRef(hasStored(storageKey))
  // SA-1034 PR 5 (spec §3.8): the ring is a landing cue. `?panel=` stays in
  // the URL after "Record feasibility result" sets it, so a ring on `focused`
  // alone lived for the life of the tab, however far the process moved. The
  // ring rides `landing`, cleared by its own timer — never by the person's
  // first scroll, which only stops the layout follow below. A section mounted
  // focused starts ringing (SA-1190): the effect runs after the commit, and
  // React 19 renders a passive effect's setState a Scheduler task later, so
  // starting at false left the mounting commit without its ring.
  const [landing, setLanding] = useState(focused)
  // SA-1418 (A2): a second press of the same `?panel=` button sends a landing request; each one
  // runs the ring and the follow below again, restarting their timers.
  const [landings, setLandings] = useState(0)
  useEffect(() => onLandingRequest(id, () => setLandings((n) => n + 1)), [id])
  useEffect(() => {
    if (!focused) { setLanding(false); return }
    setLanding(true)
    const ring = setTimeout(() => setLanding(false), FOCUS_FOLLOW_MS)
    return () => clearTimeout(ring)
  }, [focused, landings])
  useEffect(() => {
    if (!settled.current && defaultOpen) { setStored(true); settled.current = true }
  }, [defaultOpen])
  // SA-1418 (R1): an event, never a continuing state. A token that turns non-null, or names
  // another record without a remount, opens the section once; the same token on a later render
  // or refetch does nothing, so a section the person closed stays closed.
  const lastToken = useRef<string | null>(null)
  useEffect(() => {
    if (activeTaskFor && activeTaskFor !== lastToken.current) {
      setStored(true)
      settled.current = true
    }
    lastToken.current = activeTaskFor
  }, [activeTaskFor])
  const rootRef = useRef<HTMLElement | null>(null)
  const setRoot = useCallback((el: HTMLElement | null) => {
    rootRef.current = el
    if (typeof sectionRef === 'function') sectionRef(el)
    else if (sectionRef) (sectionRef as { current: HTMLElement | null }).current = el
  }, [sectionRef])
  // A deep link opens its section first (a section mounted under one already
  // starts open, above) and brings it on screen; it never writes the
  // remembered state — only the person's own toggle does. The band above
  // loads after this section mounts and pushes it down, so the scroll is
  // repeated on every layout change of the page frame for a moment, and
  // stops the instant the person moves the page themselves.
  useEffect(() => {
    if (!focused) return
    setStored(true)
    settled.current = true
    const el = rootRef.current
    if (!el) return
    // `disconnect()` stops a real ResizeObserver, but a callback already
    // in flight (or a caller that races the teardown) should still find
    // the section done following once `stop` has run.
    let stopped = false
    const scroll = () => {
      if (stopped) return
      const scroller = el.closest('main')
      if (!scroller) {
        el.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
        return
      }
      // The strip pins at the top of `main`: land the section just under it,
      // never under it. Outside a page with a sticky header the CSS margin rules.
      const sticky = scroller.querySelector<HTMLElement>('[data-slot="sticky-page-header"]')
      const scrollerTop = scroller.getBoundingClientRect().top
      const offset = sticky
        ? sticky.getBoundingClientRect().bottom - scrollerTop + STICKY_GAP_PX
        : parseFloat(getComputedStyle(el).scrollMarginTop) || 0
      const top = scroller.scrollTop + (el.getBoundingClientRect().top - scrollerTop) - offset
      scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
    }
    scroll()
    const frame = el.closest('[data-slot="page-frame"]') ?? el.parentElement
    const observer = typeof ResizeObserver !== 'undefined' && frame ? new ResizeObserver(() => scroll()) : null
    observer?.observe(frame as Element)
    const stop = () => {
      stopped = true
      observer?.disconnect()
      clearTimeout(timer)
      for (const type of USER_SCROLL_EVENTS) window.removeEventListener(type, stop)
    }
    const timer = setTimeout(stop, FOCUS_FOLLOW_MS)
    for (const type of USER_SCROLL_EVENTS) window.addEventListener(type, stop, { passive: true })
    return stop
  }, [focused, landings])
  const open = !collapsible || (!locked && stored)
  const toggle = () => {
    const next = !open
    settled.current = true
    setStored(next)
    writeStored(storageKey, next)
  }

  const quiet = chrome === 'quiet'
  const titleNode = quiet ? (
    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground">{title}</span>
  ) : (
    <span className="shrink-0 text-[12.5px] font-semibold text-foreground">{title}</span>
  )
  const summaryNode =
    summary !== undefined && summary !== null ? (
      <span
        data-testid="record-section-summary"
        aria-hidden={open || undefined}
        title={typeof summary === 'string' ? summary : undefined}
        className={cn(
          'min-w-0 flex-1 truncate text-left text-[12.5px] font-normal normal-case tracking-normal text-foreground/80',
          open && 'invisible',
        )}
      >
        {summary}
      </span>
    ) : null

  const inner = (
    <>
      <div
        className={cn(
          'flex items-center gap-2',
          wrapHeader && 'flex-wrap gap-y-1.5',
          // A quiet block's hairline is part of its shape (label, hairline, content); a section's marks the open body.
          quiet ? 'border-b border-border pb-1.5' : cn('bg-muted/40 px-3.5 py-[11px]', open && 'border-b border-border'),
        )}
      >
        {collapsible ? (
          <button
            type="button"
            onClick={toggle}
            disabled={locked}
            aria-expanded={open}
            aria-controls={open ? bodyId : undefined}
            className={wrapHeader
              ? 'flex min-w-0 flex-[1_1_auto] items-center gap-2 text-left hover:text-foreground'
              : 'flex min-w-0 flex-1 items-center gap-2 text-left hover:text-foreground'}
          >
            <ChevronDown
              aria-hidden
              className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', !open && '-rotate-90')}
            />
            {titleNode}
            {summaryNode}
          </button>
        ) : (
          <div className={wrapHeader ? 'flex min-w-0 flex-[1_1_auto] items-center gap-2' : 'flex min-w-0 flex-1 items-center gap-2'}>
            {titleNode}
            {summaryNode}
          </div>
        )}
        {actions ? (
          <div className={wrapHeader ? 'ml-auto flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2' : 'flex shrink-0 items-center gap-2'}>{actions}</div>
        ) : null}
      </div>
      {open && (
        <div id={bodyId} className={quiet ? 'pt-3' : 'px-3.5 pb-3.5 pt-3'}>
          {children}
        </div>
      )}
    </>
  )
  const rootProps = {
    ref: setRoot,
    'data-panel': id,
    ...(tour ? tourAnchor(tour) : {}),
    'data-block': chrome,
    'data-panel-focus': landing ? 'true' : undefined,
    className: cn(
      'min-w-0 scroll-mt-32',
      !quiet && 'overflow-hidden rounded-lg border border-section-border bg-card shadow-xs',
      landing && LANDING_RING,
      landing && quiet && 'rounded-md',
      className,
    ),
  }
  return aside ? <aside {...rootProps}>{inner}</aside> : <section {...rootProps}>{inner}</section>
}
